// Servicio de consulta "sin membresía" (pago de una sola cita) — replica el
// flujo de la app Flutter (`pay_extra_consultation_sheet.dart` +
// `orchestrated_slot_sheet.dart`). A nivel de producto NO existe "consulta
// extra": es simplemente pagar la cita elegida sin membresía. A nivel de
// backend el mecanismo sigue siendo `dais.extra.consultation`.
//
// Secuencia que replica (capturada de la app, usuario sin membresía):
//   1. validate_amount_pay_extra_consultation  → precio autoritativo (MXN, pesos)
//   2. get_available_processors_for_consultation_date → métodos + gating 48h
//   3. process_create_extra_consultation_by_user → crea el draft del MISMO slot
//      (con la duración EXTRA mapeada por minutos), luego se cobra con
//      payExtraConsultationWithCard (ver utils/paymentService.js).

import { isPhoneNotVerifiedResponse } from './authVerificationService';

const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN;
const ACCOUNT_ID = parseInt(process.env.REACT_APP_ACCOUNT_ID, 10) || 1;

const post = async (payload) => {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

// ─── Duraciones ────────────────────────────────────────────────────────────
// El config de especialidad puede traer las duraciones bajo varios nombres
// según la fuente (Firebase vs. campaign vs. get_model_data). Unificamos.
const includedDurationEntries = (specialty) => [
  ...(specialty?.durations || []),
  ...(specialty?.allDurations || []),
];
const extraDurationEntries = (specialty) => [
  ...(specialty?.extra_durations || []),
  ...(specialty?.allExtraDurations || []),
];

// Minutos de una duración por su id (el backend manda `duration` en minutos).
const minutesForDurationId = (specialty, durationId) => {
  if (durationId == null) return null;
  const all = [...includedDurationEntries(specialty), ...extraDurationEntries(specialty)];
  const d = all.find((x) => Number(x?.id) === Number(durationId));
  const mins = d?.duration ?? d?.minutes;
  return Number.isFinite(Number(mins)) ? Number(mins) : null;
};

// Mapea la duración INCLUIDA del slot (p.ej. 34 = 60 min) a la duración EXTRA
// de la misma especialidad con los mismos minutos (p.ej. 109). La app hace este
// mapeo estricto por minutos; `process_create_extra_consultation_by_user`
// espera el id EXTRA (verificado en la captura de red de la app).
export const resolveExtraDurationId = (specialty, includedDurationId) => {
  const extras = extraDurationEntries(specialty);
  const minutes = minutesForDurationId(specialty, includedDurationId);
  if (minutes != null) {
    const match = extras.find((d) => Number(d?.duration ?? d?.minutes) === Number(minutes));
    if (match?.id != null) return Number(match.id);
  }
  if (specialty?.extraDuration != null) return Number(specialty.extraDuration);
  if (extras[0]?.id != null) return Number(extras[0].id);
  return includedDurationId != null ? Number(includedDurationId) : null;
};

// `schedule_id` es un timestamp UNIX (segundos). El backend de procesadores
// espera la fecha/hora de la consulta en UTC con formato "YYYY-MM-DD HH:MM:SS".
export const consultationDateUtcFromScheduleId = (scheduleId) => {
  const ts = Number(scheduleId);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  const d = new Date(ts * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
  );
};

// ─── 1. Precio autoritativo (sin/ con membresía) ─────────────────────────────
// `price` viene en PESOS MXN (backend: int(round(duration.duration*rate*60))).
// `validate` === true sólo si hay membresía activa válida en la fecha.
export const validateExtraConsultationAmount = async ({ authToken, userId, durationId, scheduleId }) => {
  if (!authToken || !userId || !durationId || scheduleId == null) {
    return { success: false, error: 'Faltan params para validar el precio.' };
  }
  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken,
      res_id: 1,
      res_model: 'dais.extra.consultation',
      res_method: 'validate_amount_pay_extra_consultation',
      res_params: {
        user_id: Number(userId),
        duration_id: Number(durationId),
        schedule_id: Number(scheduleId),
      },
    },
  };
  try {
    const data = await post(payload);
    const result = data?.result || {};
    if (!result.success) {
      return { success: false, error: result.message || 'No se pudo calcular el precio.' };
    }
    return {
      success: true,
      price: Number(result.data?.price) || 0,
      currencyId: result.data?.currency_id || null,
      hasValidMembership: !!result.data?.validate,
    };
  } catch (err) {
    console.warn('[extraConsultationService.validateExtraConsultationAmount] error', err);
    return { success: false, error: err.message };
  }
};

// ─── 2. Procesadores disponibles según la fecha de la cita (gating 48h) ──────
// Métodos de tienda (transferencia / OXXO) se marcan no disponibles si la cita
// es en menos de `min_hours_ahead` (default 48h).
export const getAvailableProcessorsForConsultationDate = async ({ authToken, scheduleId, dateConsultationUtc }) => {
  const dateUtc = dateConsultationUtc || consultationDateUtcFromScheduleId(scheduleId);
  if (!dateUtc) return { success: false, items: [], minHoursAhead: 48 };

  // NOTA: este método es @api.model — NO se manda res_id (mandarlo provoca un
  // TypeError en el backend; verificado con curl contra producción).
  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken || '',
      res_model: 'payment.processor',
      res_method: 'get_available_processors_for_consultation_date',
      res_params: { date_consultation: dateUtc },
    },
  };
  try {
    const data = await post(payload);
    const result = data?.result || {};
    if (!result.success) return { success: false, items: [], minHoursAhead: 48 };
    const items = Array.isArray(result.data?.items) ? result.data.items : [];
    return {
      success: true,
      minHoursAhead: Number(result.data?.min_hours_ahead) || 48,
      items: items.map((it) => ({
        id: it.id,
        title: it.title || '',
        code: it.payment_processor?.code || '',
        methodKey: it.key || '',
        isCard: !!it.is_card_method,
        available: it.available !== false,
        unavailableReason: it.unavailable_reason || null,
      })),
    };
  } catch (err) {
    console.warn('[extraConsultationService.getAvailableProcessorsForConsultationDate] error', err);
    return { success: false, items: [], minHoursAhead: 48 };
  }
};

// ─── 3. Crear el draft de la cita (mismo slot, duración extra) ───────────────
// Devuelve { success, consultationId, data, phoneNotVerified, error }. El gate
// V2 de WhatsApp (PHONE_NOT_VERIFIED) se reporta para que el caller lo maneje.
export const createExtraConsultation = async ({
  authToken,
  userId,
  doctorId,
  date,
  extraDurationId,
  scheduleId,
  reason = '',
  reminder = true,
  consultationReasonId = null,
}) => {
  if (!authToken || !userId || !doctorId || !extraDurationId) {
    return { success: false, error: 'Faltan datos para crear la consulta.' };
  }
  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken,
      res_id: 1,
      res_model: 'dais.extra.consultation',
      res_method: 'process_create_extra_consultation_by_user',
      res_params: {
        doctor_id: Number(doctorId),
        user_id: Number(userId),
        date: typeof date === 'string' ? date : new Date(date).toISOString().split('T')[0],
        duration_id: Number(extraDurationId),
        schedule_id: scheduleId != null ? Number(scheduleId) : null,
        reason: reason || '',
        reminder: !!reminder,
        consultation_reason_id: consultationReasonId,
      },
    },
  };
  try {
    const data = await post(payload);
    const result = data?.result || {};
    if (result.success || result.id || result.consultation_id || result.data?.id) {
      return {
        success: true,
        consultationId: result.consultation_id || result.id || result.data?.id,
        data,
      };
    }
    return {
      success: false,
      phoneNotVerified: isPhoneNotVerifiedResponse(data),
      error: result.error || result.message || 'No se pudo crear la consulta.',
    };
  } catch (err) {
    console.warn('[extraConsultationService.createExtraConsultation] error', err);
    return { success: false, error: err.message };
  }
};

const extraConsultationService = {
  resolveExtraDurationId,
  consultationDateUtcFromScheduleId,
  validateExtraConsultationAmount,
  getAvailableProcessorsForConsultationDate,
  createExtraConsultation,
};

export default extraConsultationService;
